import React from "react";
import { useSearchParams } from "react-router-dom";
import { PAGE } from "src/constants/searchParams";

export const useFilters = (
  key: string,
  defaultValue?: string,
): [string, (value: string) => void] => {
  const [params, setSearchParams] = useSearchParams();
  const initializedRef = React.useRef(false);
  const filter = params.get(key) ?? "";

  const setFilter = React.useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev.toString());

        if (value.length === 0) {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }

        // Reset pagination whenever a filter changes,
        // unless this hook is managing PAGE itself.
        if (key !== PAGE) {
          newParams.set(PAGE, "0");
        }

        return newParams;
      });
    },
    [key, setSearchParams],
  );

  React.useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (!params.has(key) && defaultValue != null) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev.toString());
        if (!newParams.has(key)) {
          newParams.set(key, defaultValue);
        }
        return newParams;
      });
    }
  }, [defaultValue, key, params, setSearchParams]);

  return React.useMemo(() => {
    return [filter, setFilter];
  }, [filter, setFilter]);
};
